import { useEffect, useRef } from "react"
import {
  loadViewMediaScript,
  ANALYTIC_TYPE_TO_EVENT,
  PLAYER_STATE_TO_EVENT,
} from "../utils/viewMediaPlayer"
import { useLogEducationalVideoPlayerEvent } from "./useEducationalVideo"

const STATE_POLL_INTERVAL_MS = 2000
const BOOT_FALLBACK_DELAY_MS = 1500
const API_CALL_TIMEOUT_MS = 2000

const withTimeout = (promise, ms = API_CALL_TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms)
    ),
  ])

// ViewMedica's own analytics payload sends percent as "50%", not 50 — strip
// anything that isn't part of the number before it reaches the API, which
// expects a clean number.
const toNumber = (value) => {
  if (value === undefined || value === null) return undefined
  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""))
  return Number.isNaN(parsed) ? undefined : parsed
}

/*
  Wires one <iframe> playing a ViewMedica video up to the two event channels
  the vendor exposes (see public/viewmedica-event-test.html, the QA harness
  this hook replaces): the analytics postMessage stream, and state polling
  for play/pause/waiting transitions ViewMedica does not push on its own.

  `enabled: false` (admin preview opens) no-ops entirely — no script load, no
  listener, no polling, no events sent.
*/
export const useVideoPlaybackTracking = ({
  iframeRef,
  uuid,
  videoId,
  source,
  trackingIds,
  enabled,
}) => {
  const { mutate: logEvent } = useLogEducationalVideoPlayerEvent()
  const logEventRef = useRef(logEvent)
  logEventRef.current = logEvent

  useEffect(() => {
    if (!enabled || !uuid || !videoId || !iframeRef.current) return

    const iframe = iframeRef.current
    let cancelled = false
    let pollIntervalId = null
    let api = null
    let primaryItem = null
    let isPrimaryItem = false
    // Stamped at capture time so the backend can reconstruct the true order
    // of events: each one is an independent fire-and-forget POST, and two
    // milliseconds apart can reach the database the wrong way round.
    let sequence = 0

    // getCurrentItem() is only unreliable while the intro/logo item is
    // playing — once it resolves to the primary item, it stays reliable, so
    // isPrimaryItem only ever latches true and is never re-checked after.
    const isPrimaryVideo = async () => {
      if (isPrimaryItem) return true
      if (!api) return false
      if (!primaryItem) {
        primaryItem = await withTimeout(api.getPrimaryItem()).catch(() => null)
        if (!primaryItem) return false
      }
      const currentItem = await withTimeout(api.getCurrentItem()).catch(
        () => null
      )
      if (currentItem && currentItem.file === primaryItem.file) {
        isPrimaryItem = true
      }
      return isPrimaryItem
    }

    const send = (eventType, extra = {}) => {
      logEventRef.current({
        uuid,
        videoId,
        source,
        eventType,
        sequence: sequence++,
        ...trackingIds,
        ...extra,
      })
    }

    const handleMessage = async (event) => {
      if (!event.origin || !event.origin.match(/ondemand\.viewmedica\.com/i)) {
        return
      }
      // A report can embed several videos, each with its own iframe/message
      // stream — without this check, one video's events could be attributed
      // to another.
      if (event.source !== iframe.contentWindow) return

      const { analyticType, payload } = event.data || {}
      if (analyticType === undefined) return

      const eventType = ANALYTIC_TYPE_TO_EVENT[analyticType]
      if (!eventType) return
      if (!(await isPrimaryVideo())) return

      send(eventType, {
        percent: toNumber(payload?.percent),
        // A step's title ("Introduction", "Symptoms", ...), not a numeric
        // index — sent through as-is, same as label.
        step: payload?.step,
        label: payload?.label,
      })
    }

    window.addEventListener("message", handleMessage)

    const startStatePolling = (api) => {
      let lastState = null
      let lastTime = null
      let watchedSeconds = 0

      pollIntervalId = setInterval(async () => {
        if (document.hidden) return

        try {
          const [state, currentTime, duration] = await Promise.all([
            withTimeout(api.getPlayerState()),
            withTimeout(api.getCurrentTime()),
            withTimeout(api.getDuration()),
          ])

          if (
            state === 1 &&
            lastState === 1 &&
            lastTime !== null &&
            currentTime > lastTime
          ) {
            watchedSeconds += currentTime - lastTime
          }

          if (state !== lastState) {
            const eventType = PLAYER_STATE_TO_EVENT[state]
            if (eventType && (await isPrimaryVideo())) {
              send(eventType, {
                currentTime,
                duration,
                watchedSeconds: Math.round(watchedSeconds),
              })
            }
          }

          lastState = state
          lastTime = currentTime
        } catch {
          // Tracking must never interrupt playback — a failed poll is simply
          // skipped, the same as a failed event send.
        }
      }, STATE_POLL_INTERVAL_MS)
    }

    const boot = () => {
      if (cancelled) return
      api = new window.OndemandPlayerApi({ frame: iframe })
      isPrimaryVideo()
      startStatePolling(api)
    }

    let booted = false
    const bootOnce = () => {
      if (booted) return
      booted = true
      boot()
    }

    loadViewMediaScript()
      .then(() => {
        if (cancelled) return
        iframe.addEventListener("load", bootOnce)
        // Fallback in case the iframe's "load" already fired before this
        // listener attached.
        setTimeout(bootOnce, BOOT_FALLBACK_DELAY_MS)
      })
      .catch((error) => {
        console.warn("Failed to load ViewMedica player script:", error)
      })

    return () => {
      cancelled = true
      window.removeEventListener("message", handleMessage)
      iframe.removeEventListener("load", bootOnce)
      if (pollIntervalId) clearInterval(pollIntervalId)
    }
  }, [enabled, uuid, videoId, source, trackingIds, iframeRef])
}
